import { useState } from 'react'
import type { CSSProperties } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ThreadList } from './ThreadList'
import { ThreadDetail } from './ThreadDetail'
import { NewThreadComposer } from './NewThreadComposer'
import { cn } from '@/lib/utils'

type View =
  | { type: 'list' }
  | { type: 'thread'; threadId: string }
  | { type: 'new' }

interface SupportChatPanelProps {
  open: boolean
  onClose: () => void
  /**
   * Where the panel docks. 'floating' keeps the legacy bottom-right position;
   * 'sidebar' anchors it just to the right of the left app sidebar's edge
   * (the caller supplies the horizontal offset via `style.left`).
   */
  anchor?: 'floating' | 'sidebar'
  style?: CSSProperties
}

export function SupportChatPanel({ open, onClose, anchor = 'floating', style }: SupportChatPanelProps) {
  const [view, setView] = useState<View>({ type: 'list' })

  function handleSelectThread(threadId: string) {
    setView({ type: 'thread', threadId })
  }

  function handleNewThread() {
    setView({ type: 'new' })
  }

  function handleBackToList() {
    setView({ type: 'list' })
  }

  function handleThreadCreated(threadId: string) {
    setView({ type: 'thread', threadId })
  }

  return (
    <div
      data-html2canvas-ignore
      data-support-chat
      role="dialog"
      aria-label="Support"
      aria-hidden={!open}
      inert={!open}
      style={anchor === 'sidebar' ? style : undefined}
      className={cn(
        // The sidebar anchor renders this inside the sidebar, whose light
        // sidebar-foreground would otherwise leak into every uncolored element.
        'fixed z-50 flex flex-col rounded-lg border bg-background text-foreground shadow-xl transition-all duration-200 ease-out',
        'w-[400px] h-[520px]',
        anchor === 'sidebar' ? 'bottom-4' : 'bottom-20 right-6',
        'max-sm:left-0 max-sm:right-0 max-sm:bottom-0 max-sm:w-full max-sm:h-[100dvh] max-sm:rounded-none',
        open
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 translate-y-2 pointer-events-none',
      )}
    >
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <h2 className="text-sm font-semibold">Support</h2>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7" aria-label="Close support">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <Separator />
      <div className="min-h-0 flex-1 overflow-hidden pt-3">
        {view.type === 'list' && (
          <ThreadList open={open} onSelectThread={handleSelectThread} onNewThread={handleNewThread} />
        )}
        {view.type === 'thread' && (
          <ThreadDetail threadId={view.threadId} onBack={handleBackToList} />
        )}
        {view.type === 'new' && (
          <NewThreadComposer onBack={handleBackToList} onThreadCreated={handleThreadCreated} />
        )}
      </div>
    </div>
  )
}
